import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Plus } from 'lucide-react'
import { useProject } from './ProjectLayout'
import { StoryDrawer } from './StoryDrawer'
import { NewStoryDialog } from './NewStoryDialog'
import {
  Avatar,
  Button,
  Card,
  Chip,
  cn,
  statusTone,
  priorityTone,
} from '../../design'
import { listStories, listWorkflowStates, type Story, type StoryPriority } from '../../api/stories'
import { lookupEpics } from '../../api/users'

export default function Backlog() {
  const project = useProject()
  const [params, setParams] = useSearchParams()
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  const storyKey = params.get('story')
  const q = params.get('q') ?? ''
  const epicId = params.get('epic') ?? ''
  const priority = params.get('priority') ?? ''
  const statusCat = params.get('status') ?? ''

  const wfQ = useQuery({
    queryKey: ['workflow-states', project.id],
    queryFn: () => listWorkflowStates(project.id),
    staleTime: 60_000,
  })
  const epicsQ = useQuery({
    queryKey: ['lookups', 'epics', project.id],
    queryFn: () => lookupEpics(project.id),
    staleTime: 60_000,
  })

  const statusIds = useMemo(() => {
    if (!statusCat) return undefined
    return wfQ.data?.filter(s => s.category === statusCat).map(s => s.id).join(',')
  }, [statusCat, wfQ.data])

  const storiesQ = useQuery({
    queryKey: ['stories', 'backlog', project.id, q, epicId, priority, statusCat, statusIds],
    queryFn: () => listStories({
      project_id: project.id,
      q: q || undefined,
      epic_id: epicId ? Number(epicId) : undefined,
      priority: (priority as StoryPriority) || undefined,
      sort: 'sort_order,-created_at',
      per_page: 100,
      ...(statusIds ? { status_id: Number(statusIds.split(',')[0]) } : {}),
    }),
    enabled: !statusCat || wfQ.data !== undefined,
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  })

  function setParam(k: string, v: string) {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v); else next.delete(k)
    setParams(next, { replace: true })
  }

  function openStory(key: string) {
    const next = new URLSearchParams(params)
    next.set('story', key)
    setParams(next, { replace: true })
    setOpen(true)
  }
  function closeStory() {
    setOpen(false)
    const next = new URLSearchParams(params)
    next.delete('story')
    setParams(next, { replace: true })
  }

  // If URL has ?story=KEY on first render, open drawer
  useMemo(() => {
    if (storyKey && !open) setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyKey])

  const stories: Story[] = storiesQ.data?.data ?? []

  return (
    <div className="mx-auto max-w-7xl px-6 py-6 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Backlog</h2>
          <p className="text-xs text-fg-subtle">
            All stories in {project.key}. Click any row to open detail.
          </p>
        </div>
        <Button size="md" leftIcon={<Plus size={14} />} onClick={() => setCreating(true)}>New story</Button>
        <NewStoryDialog
          projectId={project.id}
          open={creating}
          onOpenChange={setCreating}
          onCreated={story => openStory(story.key)}
        />
      </div>

      {/* Filters */}
      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-3 p-3">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle" />
            <input
              value={q}
              onChange={e => setParam('q', e.target.value)}
              placeholder="Search title or key…"
              className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border bg-bg text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
          </div>

          <select
            value={statusCat}
            onChange={e => setParam('status', e.target.value)}
            className="rounded-md border border-border bg-bg-elevated text-sm px-2.5 py-1.5"
          >
            <option value="">All status</option>
            <option value="todo">Todo</option>
            <option value="in_progress">In Progress</option>
            <option value="in_review">In Review</option>
            <option value="done">Done</option>
          </select>

          <select
            value={epicId}
            onChange={e => setParam('epic', e.target.value)}
            className="rounded-md border border-border bg-bg-elevated text-sm px-2.5 py-1.5"
          >
            <option value="">All epics</option>
            {(epicsQ.data ?? []).map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>

          <select
            value={priority}
            onChange={e => setParam('priority', e.target.value)}
            className="rounded-md border border-border bg-bg-elevated text-sm px-2.5 py-1.5"
          >
            <option value="">All priorities</option>
            <option value="high">High</option>
            <option value="med">Med</option>
            <option value="low">Low</option>
          </select>

          {(q || statusCat || epicId || priority) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setParams({}, { replace: true })}
            >
              Clear
            </Button>
          )}

          <span className="ml-auto text-xs text-fg-subtle font-mono">
            {storiesQ.isLoading ? '…' : `${stories.length} stories`}
          </span>
        </div>
      </Card>

      {/* Story table */}
      <Card padded={false}>
        {storiesQ.isLoading && (
          <div className="px-5 py-10 text-center text-fg-subtle text-sm">Loading…</div>
        )}
        {!storiesQ.isLoading && stories.length === 0 && (
          <div className="px-5 py-10 text-center text-fg-subtle text-sm">
            No stories match your filters.
          </div>
        )}
        <ul className="divide-y divide-border">
          {stories.map(s => (
            <li
              key={s.id}
              onClick={() => openStory(s.key)}
              className={cn(
                'px-5 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-bg-muted/40',
              )}
            >
              <span className="font-mono text-xs text-fg-subtle w-16 shrink-0">{s.key}</span>
              <span className="text-sm text-fg flex-1 truncate">{s.title}</span>
              {s.epic_name && (
                <Chip size="sm" tone="epic" color={s.epic_color ?? '#9aa0a6'} dot>
                  {s.epic_name}
                </Chip>
              )}
              {s.assignee_name ? (
                <Avatar size="xs" name={s.assignee_name} color={s.assignee_avatar_color ?? '#9aa0a6'} />
              ) : (
                <span className="w-5" />
              )}
              <Chip size="sm" tone={priorityTone(s.priority)}>{s.priority}</Chip>
              <Chip size="sm" tone={statusTone(s.status_category)}>{s.status_name}</Chip>
              {s.estimate_points !== null && (
                <span className="font-mono text-[10px] text-fg-subtle w-6 text-right">{s.estimate_points}</span>
              )}
              {s.due_date && (
                <span className="text-[10px] text-fg-subtle w-14 text-right">
                  {new Date(s.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <StoryDrawer
        storyKey={storyKey}
        projectId={project.id}
        open={open}
        onOpenChange={(o) => { if (!o) closeStory(); else setOpen(true) }}
      />
    </div>
  )
}
